import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Steps, Button, Input, Select, InputNumber, DatePicker, Radio, Row, Col,
  Space, message, Table, Upload, Spin, AutoComplete, Divider } from 'antd';
import { ArrowLeftOutlined, FilePdfOutlined, UploadOutlined, DeleteOutlined,
  StarOutlined, StarFilled } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../services/api';
import useAvisoAlteracoes from '../hooks/useAvisoAlteracoes';
import ManometroForm from '../components/ManometroForm';
import ValvulaForm from '../components/ValvulaForm';

const { Option } = Select;
const { TextArea } = Input;

const TIPO_LAUDO_FIXO = 'Laudo de Inspeção Externa NR-13';
const UNI_P = [['kgf', 'kgf/cm²'], ['bar', 'bar'], ['psi', 'psi'], ['mpa', 'MPa']];
const EQUIPAMENTOS = ['Vaso de Pressão', 'Autoclave', 'Gerador de Vapor', 'Câmara de Esterilização'];
const CNC = [{ value: 'C', label: 'C' }, { value: 'NC', label: 'NC' }, { value: 'NA', label: 'N/A' }];

// Tabela 1 da NR-13. A categoria DECORRE de grupo e classe — nao e digitada,
// senao da para gravar uma combinacao que a norma nao permite.
const CATEGORIA_NR13 = {
  A: { 1: 'I', 2: 'I', 3: 'II', 4: 'III', 5: 'III' },
  B: { 1: 'I', 2: 'II', 3: 'III', 4: 'IV', 5: 'IV' },
  C: { 1: 'I', 2: 'II', 3: 'III', 4: 'IV', 5: 'V' },
  D: { 1: 'II', 2: 'III', 3: 'IV', 4: 'V', 5: 'V' },
};
const categoriaDe = (classe, grupo) =>
  (CATEGORIA_NR13[classe] && CATEGORIA_NR13[classe][parseInt(grupo, 10)]) || null;

const INSTALACAO = [
  ['cl_acesso_manutencao', 'b) Acesso fácil e seguro para manutenção, operação e inspeção'],
  ['cl_ventilacao_permanente', 'c) Ventilação permanente com entradas de ar que não possam ser bloqueadas'],
  ['cl_iluminacao_normas', 'd) Iluminação conforme normas oficiais vigentes'],
  ['cl_iluminacao_emergencia', 'e) Sistema de iluminação de emergência'],
];

const CHECKLIST = [
  ['cl_bases_assentamento', 'Inspeção das bases de assentamento do vaso'],
  ['cl_pintura', 'Inspeção da pintura'],
  ['cl_bocais_soldados', 'Inspeção dos bocais soldados ao recipiente'],
  ['cl_aterramento', 'Verificação do aterramento do vaso'],
  ['cl_fixacao_base', 'Inspeção da fixação da base'],
  ['cl_inspecao_solda', 'Inspeção Visual da solda'],
  ['cl_chapas', 'Inspeção das chapas'],
  ['cl_boca_visita', 'Inspeção da boca de visita/bocal do recipiente'],
  ['cl_aneis_juntas', 'Inspeção dos anéis e juntas de vedação dos bocais'],
  ['cl_iluminacao_emergencia', 'Iluminação de emergência (item OBRIGATÓRIO)'],
  ['cl_extintor', 'Extintor (obrigatório para fluidos combustíveis e inflamáveis)'],
  ['cl_valvulas_manometro', 'Verificação das válvulas de segurança e manômetro'],
  ['cl_pressostatos', 'Verificação de pressostatos'],
  ['cl_medidor_nivel', 'Verificação do Medidor de Nível'],
  ['cl_termometro', 'Verificação do Termômetro'],
  ['cl_dreno', 'Verificação do Dreno'],
  ['cl_placa_identificacao', 'Placa de identificação'],
];

const artigoEquip = (tipo) => {
  const t = String(tipo || '').toLowerCase();
  if (t.indexOf('gerador') >= 0) return 'no gerador de vapor';
  if (t.indexOf('mara') >= 0) return 'na câmara de esterilização';
  if (t.indexOf('autoclave') >= 0) return 'na autoclave';
  return 'no vaso de pressão';
};
const uniLabel = (u) => (UNI_P.find((x) => x[0] === (u || 'kgf')) || UNI_P[0])[1];

const textoConclusao = (d) => {
  const pmta = d.pmta ? String(d.pmta).replace('.', ',') + ' ' + uniLabel(d.pmta_unidade) : '____';
  return 'De acordo com a inspeção realizada ' + artigoEquip(d.tipo_equipamento) +
    ', alinhada aos parâmetros estabelecidos na NR-13, pode-se concluir que o equipamento se encontra APTO, ' +
    'e poderá continuar em operação desde que:\n' +
    'Respeitando a PMTA podendo estar igual ou inferior a ' + pmta + '. ' +
    'Na eventualidade de alguma alteração em alguma das características do equipamento, ' +
    'uma inspeção extraordinária deve ser realizada pois este não terá mais validade.';
};

export default function InspecaoExterna() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const naoSalvo = useAvisoAlteracoes(d);
  const [step, setStep] = useState(0);
  const [salvando, setSalvando] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [cnpjs, setCnpjs] = useState([]);
  const [fluidos, setFluidos] = useState([]);
  const [imgs, setImgs] = useState({});
  const [enviandoFoto, setEnviandoFoto] = useState(false);

  const set = (campo, v) => setD((x) => ({ ...x, [campo]: v }));

  const carregarImagem = useCallback(async (idx) => {
    try {
      const r = await api.get('/inspecoes-externas/' + id + '/fotos/' + idx, { responseType: 'blob' });
      setImgs((p) => ({ ...p, [idx]: URL.createObjectURL(r.data) }));
    } catch (e) { /* miniatura fica vazia */ }
  }, [id]);

  const carregar = useCallback(async () => {
    try {
      const r = await api.get('/inspecoes-externas/' + id);
      setD(r.data);
      (r.data.fotos || []).forEach((f, i) => carregarImagem(i));
    } catch (e) { message.error('Erro ao carregar o laudo'); }
  }, [id, carregarImagem]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    api.get('/clientes/cnpjs/todos').then((r) => setCnpjs(r.data || [])).catch(() => {});
    api.get('/classificacao-nr13/fluidos').then((r) => setFluidos(r.data || [])).catch(() => {});
  }, []);

  const salvar = async (aviso = true) => {
    if (!d) return;
    setSalvando(true);
    try {
      const corpo = { ...d };
      ['manometros', 'valvulas', 'fotos', 'razao_social', 'cnpj', 'logradouro_fiscal',
        'numero_fiscal', 'bairro_fiscal', 'cidade_fiscal', 'estado_fiscal', 'cep_fiscal',
        'id', 'codigo', 'criado_em', 'atualizado_em', 'disponivel_portal', 'app_uuid']
        .forEach((k) => delete corpo[k]);
      corpo.tipo_laudo = TIPO_LAUDO_FIXO;
      const r = await api.put('/inspecoes-externas/' + id, corpo);
      // o servidor recompoe as nao conformidades no texto da conclusao
      if (r.data && r.data.observacoes !== corpo.observacoes) set('observacoes', r.data.observacoes);
      naoSalvo.limpo();
      if (aviso) message.success('Salvo');
    } catch (e) { message.error('Erro ao salvar'); }
    setSalvando(false);
  };

  const irPara = async (n) => { await salvar(false); setStep(n); };

  const calcularPrazo = async () => {
    if (!d.data_inspecao) { message.warning('Preencha a data da inspeção primeiro.'); return; }
    if (!d.classe || !d.grupo) { message.warning('Preencha a classe do fluido e o grupo.'); return; }
    try {
      const r = await api.post('/laudos/calcular-prazos', {
        categoria: d.categoria, classe: d.classe, grupo: d.grupo,
        duas_saidas: d.duas_saidas, equipamento_movel: d.equipamento_movel,
        data_inspecao: String(d.data_inspecao).substring(0, 10)
      });
      if (r.data.proxima_inspecao_externa) {
        set('proxima_inspecao_externa', r.data.proxima_inspecao_externa);
        message.success(d.duas_saidas === false
          ? 'Prazo calculado, já com a redução de um ano por não ter duas saídas amplas.'
          : 'Prazo calculado.');
        if (d.duas_saidas === false && d.equipamento_movel === true) {
          message.info('Equipamento móvel: a regra das duas saídas não se aplica.');
        }
      }
    } catch (e) { message.error('Erro ao calcular o prazo'); }
  };

  const gerarPDF = async () => {
    setGerando(true);
    message.loading({ content: 'Gerando PDF...', key: 'pdf', duration: 0 });
    try {
      const r = await api.get('/inspecoes-externas/' + id + '/pdf', { responseType: 'blob' });
      window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
      message.destroy('pdf');
      carregar();
    } catch (e) {
      message.destroy('pdf');
      let msg = 'Erro ao gerar o PDF';
      try { msg = JSON.parse(await e.response.data.text()).erro || msg; } catch (x) {}
      message.error(msg);
    }
    setGerando(false);
  };

  const subirFoto = async (file) => {
    const fd = new FormData();
    fd.append('foto', file);
    setEnviandoFoto(true);
    try { await api.post('/inspecoes-externas/' + id + '/fotos', fd); await carregar(); }
    catch (e) { message.error('Erro ao enviar a foto'); }
    setEnviandoFoto(false);
    return false;
  };

  const definirCapa = async (idx) => {
    try { await api.put('/inspecoes-externas/' + id + '/fotos/' + idx + '/principal'); await carregar(); }
    catch (e) { message.error('Erro ao definir a capa'); }
  };

  const removerFoto = async (idx) => {
    try { await api.delete('/inspecoes-externas/' + id + '/fotos/' + idx); setImgs({}); await carregar(); }
    catch (e) { message.error('Erro ao remover a foto'); }
  };

  if (!d) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;

  const cnpjSel = cnpjs.find((c) => c.id === d.cnpj_faturamento_id);
  const categoriaCalc = categoriaDe(d.classe, d.grupo);

  const simNao = (campo, rotulo) => (
    <Col span={6}>
      <label>{rotulo}</label>
      <Select style={{ width: '100%' }} allowClear placeholder="—"
        value={d[campo] === true ? true : d[campo] === false ? false : undefined}
        onChange={(v) => set(campo, v === undefined ? null : v)}
        options={[{ value: true, label: 'Sim' }, { value: false, label: 'Não' }]} />
    </Col>
  );

  const tabelaCNC = (lista) => (
    <Table rowKey={(r) => r[0]} size="small" pagination={false} dataSource={lista}
      columns={[
        { title: 'Item', render: (_, r) => r[1] },
        { title: 'Resultado', width: 190, render: (_, r) => (
          <Radio.Group size="small" optionType="button" options={CNC}
            value={d[r[0]] || undefined} onChange={(e) => set(r[0], e.target.value)} />
        ) },
      ]} />
  );

  const campoPressao = (campo, rotulo) => (
    <Col span={8}>
      <label>{rotulo}</label>
      <Space.Compact style={{ width: '100%' }}>
        <InputNumber style={{ width: '65%' }} precision={2} decimalSeparator=","
          value={d[campo]} onChange={(v) => set(campo, v)} />
        <Select style={{ width: '35%' }} value={d[campo + '_unidade'] || 'kgf'}
          onChange={(v) => set(campo + '_unidade', v)}>
          {UNI_P.map(([k, r]) => <Option key={k} value={k}>{r}</Option>)}
        </Select>
      </Space.Compact>
    </Col>
  );

  const etapas = [{ title: 'Identificação' }, { title: 'Equipamento' }, { title: 'Instalação' },
    { title: 'Manômetro' }, { title: 'Válvula' }, { title: 'Check list' },
    { title: 'Fotos' }, { title: 'Resultado' }];

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/laudos')}>Voltar</Button>
        <strong style={{ fontSize: 16 }}>
          Inspeção Externa NR-13 {d.codigo ? '— ' + d.codigo : ''} {d.tag ? '· ' + d.tag : ''}
        </strong>
        <Button loading={salvando} onClick={() => salvar()}>Salvar</Button>
        <Button type="primary" icon={<FilePdfOutlined />} loading={gerando} onClick={gerarPDF}>
          Gerar PDF</Button>
      </Space>

      <Steps current={step} size="small" items={etapas} onChange={irPara} style={{ marginBottom: 16 }} />

      {step === 0 && (
        <Card size="small" title="Identificação">
          <Row gutter={16}>
            <Col span={8}><label>Cliente</label><Input value={d.razao_social || ''} disabled /></Col>
            <Col span={8}><label>CNPJ do Laudo</label>
              <Select style={{ width: '100%' }} showSearch allowClear placeholder="Pesquise pelo CNPJ"
                optionFilterProp="children" value={d.cnpj_faturamento_id || undefined}
                disabled={!!(d.os_id || d.numero_os)}
                onChange={(v) => set('cnpj_faturamento_id', v || null)}>
                {cnpjs.map((c) => (
                  <Option key={c.id} value={c.id}>
                    {c.cnpj} — {c.razao_social} {c.cliente_nome ? '(' + c.cliente_nome + ')' : ''}
                  </Option>
                ))}
              </Select></Col>
            <Col span={8}><label>ART do Cliente (CREA-MG)</label>
              <Input value={cnpjSel ? (cnpjSel.art_numero || '') : (d.art_numero || '')} disabled
                style={{ background: '#f6ffed', color: '#135200', fontWeight: 'bold' }} /></Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 12 }}>
            <Col span={8}><label>Tipo de Laudo</label>
              <Input value={TIPO_LAUDO_FIXO} disabled /></Col>
            <Col span={5}><label>Número da OS</label><Input value={d.numero_os || ''} disabled /></Col>
            <Col span={5}><label>Data da inspeção *</label>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                value={d.data_inspecao ? dayjs(String(d.data_inspecao).substring(0, 10)) : null}
                onChange={(v) => set('data_inspecao', v ? v.format('YYYY-MM-DD') : null)} /></Col>
            <Col span={6}><label>Técnico responsável</label><Input value={d.tecnico || ''} disabled /></Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 12 }}>
            <Col span={24}><label>Endereço de Atendimento</label>
              <Input value={d.endereco_atendimento || ''} disabled /></Col>
          </Row>
        </Card>
      )}

      {step === 1 && (
        <Card size="small" title="Equipamento — todos os dados preenchidos manualmente">
          <Row gutter={16}>
            <Col span={6}><label>Tipo de Inspeção</label>
              <Select style={{ width: '100%' }} value={d.tipo_inspecao_periodicidade || undefined}
                onChange={(v) => set('tipo_inspecao_periodicidade', v)}
                options={[{ value: 'inicial', label: 'Inicial' }, { value: 'periodica', label: 'Periódica' },
                  { value: 'extraordinaria', label: 'Extraordinária' }]} /></Col>
            <Col span={6}><label>Tipo de Equipamento</label>
              <AutoComplete style={{ width: '100%' }} placeholder="Escolha ou digite"
                options={EQUIPAMENTOS.map((t) => ({ value: t }))}
                value={d.tipo_equipamento || ''} onChange={(v) => set('tipo_equipamento', v)} /></Col>
            <Col span={4}><label>TAG</label>
              <Input value={d.tag || ''} placeholder="VP-01"
                onChange={(e) => set('tag', (e.target.value || '').toUpperCase())} /></Col>
            <Col span={4}><label>Posição</label>
              <Select style={{ width: '100%' }} value={d.posicao || undefined} onChange={(v) => set('posicao', v)}
                options={[{ value: 'vertical', label: 'Vertical' }, { value: 'horizontal', label: 'Horizontal' }]} /></Col>
            <Col span={4}><label>Número de Série</label>
              <Input value={d.numero_serie || ''} onChange={(e) => set('numero_serie', e.target.value)} /></Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 12 }}>
            <Col span={5}><label>Fabricante</label>
              <Input value={d.fabricante || ''} onChange={(e) => set('fabricante', e.target.value)} /></Col>
            <Col span={4}><label>Ano Fabricação</label>
              <InputNumber style={{ width: '100%' }} min={1900} max={2100}
                value={d.ano_fabricacao ? parseInt(d.ano_fabricacao, 10) : null}
                onChange={(v) => set('ano_fabricacao', v ? String(v) : null)} /></Col>
            <Col span={5}><label>Modelo</label>
              <Input value={d.modelo || ''} onChange={(e) => set('modelo', e.target.value)} /></Col>
            <Col span={5}><label>ASME VIII, DIV. I</label>
              <Input placeholder="Ex: tabela 2021" value={d.asm || ''}
                onChange={(e) => set('asm', e.target.value)} /></Col>
            <Col span={5}><label>Temperatura (°C)</label>
              <InputNumber style={{ width: '100%' }} decimalSeparator=","
                value={d.temperatura} onChange={(v) => set('temperatura', v)} /></Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 12 }}>
            <Col span={6}><label>Fluido</label>
              <Select style={{ width: '100%' }} showSearch optionFilterProp="children"
                value={d.fluido || undefined} onChange={(v) => set('fluido', v)}>
                {fluidos.map((f) => (
                  <Option key={f.id} value={f.nome}>{f.nome} (Classe {f.classe})</Option>
                ))}
                <Option value="outro">Outro</Option>
              </Select></Col>
            {d.fluido === 'outro' && (
              <Col span={6}><label>Fluido (outro)</label>
                <Input value={d.fluido_outro || ''} onChange={(e) => set('fluido_outro', e.target.value)} /></Col>
            )}
            <Col span={6}><label>Capacidade volumétrica</label>
              <Space.Compact style={{ width: '100%' }}>
                <InputNumber style={{ width: '65%' }} precision={2} decimalSeparator=","
                  value={d.volume} onChange={(v) => set('volume', v)} />
                <Select style={{ width: '35%' }} value={d.volume_unidade || 'L'}
                  onChange={(v) => set('volume_unidade', v)}
                  options={[{ value: 'L', label: 'L' }, { value: 'm3', label: 'm³' }]} />
              </Space.Compact></Col>
            <Col span={12}><label>Local de Instalação</label>
              <Input value={d.local_instalacao || ''} onChange={(e) => set('local_instalacao', e.target.value)} /></Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 12 }}>
            {campoPressao('pressao_operacao', 'Pressão de operação (PMO)')}
            {campoPressao('pmta', 'PMTA')}
            {campoPressao('pth', 'Pressão de teste hidrostático (PTH)')}
          </Row>
          <Divider style={{ margin: '14px 0 10px' }}>Classificação na NR-13</Divider>
          <Row gutter={16}>
            <Col span={6}><label>Classe do fluido</label>
              <Select style={{ width: '100%' }} value={d.classe || undefined}
                onChange={(v) => { set('classe', v); set('categoria', categoriaDe(v, d.grupo)); }}
                options={['A', 'B', 'C', 'D'].map((v) => ({ value: v, label: v }))} /></Col>
            <Col span={6}><label>Grupo</label>
              <Select style={{ width: '100%' }} value={d.grupo || undefined}
                onChange={(v) => { set('grupo', v); set('categoria', categoriaDe(d.classe, v)); }}
                options={['1', '2', '3', '4', '5'].map((v) => ({ value: v, label: v }))} /></Col>
            <Col span={6}><label>Categoria</label>
              <Input value={categoriaCalc || ''} disabled
                style={{ background: '#f6ffed', color: '#135200', fontWeight: 'bold' }} />
              <div style={{ fontSize: 12, color: '#888' }}>Vem da Tabela 1 da NR-13</div></Col>
          </Row>
        </Card>
      )}

      {step === 2 && (
        <Card size="small" title="Condições de instalação">
          <Row gutter={16} style={{ marginBottom: 12 }}>
            <Col span={6}><label>Instalação</label>
              <Select style={{ width: '100%' }} allowClear placeholder="—"
                value={d.instalacao_aberta === true ? true : d.instalacao_aberta === false ? false : undefined}
                onChange={(v) => set('instalacao_aberta', v === undefined ? null : v)}
                options={[{ value: true, label: 'Aberta' }, { value: false, label: 'Fechada' }]} /></Col>
            {simNao('duas_saidas', 'a) Duas saídas amplas?')}
            {simNao('equipamento_movel', 'Equipamento Móvel?')}
            {simNao('possui_livro', 'Possui livro?')}
          </Row>
          <Row gutter={16} style={{ marginBottom: 12 }}>
            {simNao('possui_prontuario', 'Possui prontuário?')}
            <Col span={18} style={{ paddingTop: 22, fontSize: 12, color: '#888' }}>
              Responder "Não" em duas saídas reduz um ano no prazo da próxima inspeção externa,
              e o laudo passa a informar isso. Deixar em branco não aplica redução nenhuma.
            </Col>
          </Row>
          {tabelaCNC(INSTALACAO)}
        </Card>
      )}

      {step === 3 && (
        <Card size="small" title="Manômetro">
          <Row gutter={16} style={{ marginBottom: 12 }}>
            {simNao('possui_manometro', 'Possui manômetro?')}
          </Row>
          {d.possui_manometro && <ManometroForm laudoId={parseInt(id)} tagVaso={d.tag || ''} origem="externa" />}
        </Card>
      )}

      {step === 4 && (
        <Card size="small" title="Válvula de segurança">
          <Row gutter={16} style={{ marginBottom: 12 }}>
            {simNao('possui_valvula', 'Possui válvula de segurança?')}
          </Row>
          {d.possui_valvula && <ValvulaForm laudoId={parseInt(id)} tagVaso={d.tag || ''} origem="externa" />}
        </Card>
      )}

      {step === 5 && <Card size="small" title="Check list">{tabelaCNC(CHECKLIST)}</Card>}

      {step === 6 && (
        <Card size="small" title="Registro fotográfico"
          extra={<Upload beforeUpload={subirFoto} showUploadList={false} multiple accept="image/*">
            <Button icon={<UploadOutlined />} loading={enviandoFoto}>
              {enviandoFoto ? 'Enviando...' : 'Adicionar fotos'}</Button></Upload>}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {(d.fotos || []).map((f, i) => (
              <div key={i} style={{ width: 160 }}>
                <div style={{ width: '100%', height: 120, borderRadius: 4,
                  border: f.principal ? '2px solid #1a3c5e' : '1px solid #ddd',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                  {imgs[i]
                    ? <img alt="" src={imgs[i]} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <Spin size="small" />}
                </div>
                <Space.Compact style={{ width: '100%', marginTop: 4 }}>
                  <Button size="small" style={{ width: '55%' }} title={f.principal ? 'Esta é a capa' : 'Usar como capa'}
                    icon={f.principal ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
                    onClick={() => definirCapa(i)}>{f.principal ? 'Capa' : 'Capa'}</Button>
                  <Button size="small" danger style={{ width: '45%' }} icon={<DeleteOutlined />}
                    onClick={() => removerFoto(i)} />
                </Space.Compact>
              </div>
            ))}
            {!(d.fotos || []).length && <span style={{ color: '#888' }}>Nenhuma foto ainda.</span>}
          </div>
        </Card>
      )}

      {step === 7 && (
        <Card size="small" title="Prazo e resultado">
          <Row gutter={16} style={{ marginBottom: 12 }}>
            <Col span={8}><label>Data da próxima inspeção externa</label>
              <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY"
                value={d.proxima_inspecao_externa ? dayjs(String(d.proxima_inspecao_externa).substring(0, 10)) : null}
                onChange={(v) => set('proxima_inspecao_externa', v ? v.format('YYYY-MM-DD') : null)} /></Col>
            <Col span={6} style={{ paddingTop: 22 }}>
              <Button onClick={calcularPrazo}>Calcular pela classificação</Button></Col>
            <Col span={10}><label>Resultado</label><br />
              <Radio.Group optionType="button" value={d.resultado || undefined}
                onChange={(e) => {
                  set('resultado', e.target.value);
                  if (e.target.value === 'aprovado' && !String(d.observacoes || '').trim()) {
                    set('observacoes', textoConclusao(d));
                  }
                }}
                options={[{ value: 'aprovado', label: 'Apto' }, { value: 'reprovado', label: 'Não apto' }]} /></Col>
          </Row>
          <label>Conclusão</label>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>
            Vem preenchido quando o resultado é Apto. Em caso de reprovação, apague e escreva o motivo.
          </div>
          <TextArea rows={8} value={d.observacoes || ''} onChange={(e) => set('observacoes', e.target.value)} />
          <Button size="small" style={{ marginTop: 6 }}
            onClick={() => set('observacoes', textoConclusao(d))}>Usar o texto padrão</Button>
        </Card>
      )}

      <Space style={{ marginTop: 16 }}>
        <Button disabled={step === 0} onClick={() => irPara(step - 1)}>Anterior</Button>
        <Button type="primary" disabled={step === etapas.length - 1}
          onClick={() => irPara(step + 1)}>Próximo</Button>
      </Space>
    </div>
  );
}
